/* ============================================================
   Admin → Leads page (stats + filters + table + drawer).
   Ported from design/admin.jsx (StatCard, SourceChart, LeadsTable,
   LeadDrawer) and design/admin2.jsx (Dashboard toolbar).
   ============================================================ */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";

import { I } from "../../lib/brand";
import {
  HttpError,
  type EmployeeAdmin,
  type LeadAdmin,
  type LeadsPage,
  type UserPublic,
  adminApi,
  adminCardImageUrl,
} from "../../lib/api";

const STR = {
  totalLeads: "Total leads",
  thisWeek: "This week",
  bySource: "Leads by source",
  search: "Search leads…",
  allEmployees: "All employees",
  allSources: "All sources",
  allTime: "All time",
  name: "Name",
  company: "Company",
  wa: "WhatsApp",
  emp: "Employee",
  source: "Source",
  captured: "Captured",
  status: "Status",
  exportCsv: "Export CSV",
  loading: "Loading leads…",
  noLeads: "No leads match your filters",
  noLeadsSub: "Try clearing a filter or widening the date range.",
  clear: "Clear",
  drawerHint: "Click any row for full detail",
  showing: "Showing",
  of: "of",
  notes: "Notes",
  email: "Email",
  empCol: "Employee",
  sourceCol: "Source",
  capturedCol: "Captured",
  noNotes: "No notes captured.",
  cardDetails: "Card details",
  title: "Title",
  mobile: "Mobile",
  landline: "Landline",
  address: "Address",
  city: "City",
  linkedin: "LinkedIn",
  website: "Website",
};

const PAGE_SIZE = 50;
const NULL_SOURCE = "—";

export default function AdminLeads() {
  const user = useOutletContext<UserPublic>();
  const isAdmin = user.role === "admin";

  const [q, setQ] = useState("");
  const [fEmp, setFEmp] = useState<string>("all");
  const [fSrc, setFSrc] = useState<string>("all");
  const [fRange, setFRange] = useState<"all" | "week">("all");
  const [offset, setOffset] = useState(0);

  const [employees, setEmployees] = useState<EmployeeAdmin[]>([]);
  const [data, setData] = useState<LeadsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LeadAdmin | null>(null);

  /* Admin needs the employees list for the filter dropdown.  Employees role
     can't list other employees (the endpoint 403s), so skip. */
  useEffect(() => {
    if (!isAdmin) return;
    adminApi.listEmployees(true).then(setEmployees).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    adminApi
      .listLeads({ q: q || undefined, limit: PAGE_SIZE, offset })
      .then(setData)
      .catch((e: unknown) => {
        setError(
          e instanceof HttpError
            ? `HTTP ${e.status}: ${e.message}`
            : e instanceof Error
              ? e.message
              : String(e),
        );
      })
      .finally(() => setLoading(false));
  }, [q, offset]);

  /* Backend already role-scopes and search-filters; employee + source + range
     are applied client-side over the current page (keeps the v1 API tight). */
  const filtered = useMemo<LeadAdmin[]>(() => {
    if (!data) return [];
    const weekCutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return data.items.filter((l) => {
      if (isAdmin && fEmp !== "all" && l.employee_id !== fEmp) return false;
      if (fSrc !== "all") {
        const key = l.source_tag ?? NULL_SOURCE;
        if (key !== fSrc) return false;
      }
      if (fRange === "week" && new Date(l.created_at) < weekCutoff) return false;
      return true;
    });
  }, [data, fEmp, fSrc, fRange, isAdmin]);

  const sources = useMemo<string[]>(() => {
    const set = new Set<string>();
    data?.items.forEach((l) => set.add(l.source_tag ?? NULL_SOURCE));
    return Array.from(set).sort();
  }, [data]);

  const weekCount = useMemo<number>(() => {
    if (!data) return 0;
    const weekCutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return data.items.filter((l) => new Date(l.created_at) >= weekCutoff).length;
  }, [data]);

  function clearFilters() {
    setQ("");
    setFEmp("all");
    setFSrc("all");
    setFRange("all");
    setOffset(0);
  }

  function exportCSV() {
    const cols: Array<{ key: keyof LeadAdmin | "employee"; label: string }> = [
      { key: "id", label: "id" },
      { key: "name", label: "name" },
      { key: "company", label: "company" },
      { key: "email", label: "email" },
      { key: "wa_id", label: "wa_id" },
      { key: "employee", label: "employee" },
      { key: "source_tag", label: "source" },
      { key: "created_at", label: "captured" },
      { key: "status", label: "status" },
    ];
    const head = cols.map((c) => c.label).join(",");
    const rows = filtered.map((l) =>
      cols
        .map((c) => {
          const v = c.key === "employee" ? l.employee_name ?? "" : (l[c.key] ?? "");
          return `"${String(v).replace(/"/g, '""')}"`;
        })
        .join(","),
    );
    const csv = [head, ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "sahab-leads.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  return (
    <>
      {/* stats strip */}
      <div style={{ display: "flex", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        <StatCard
          label={STR.totalLeads}
          value={data ? String(data.total) : "—"}
          unit="LEADS"
          sub={data ? `${STR.showing} ${filtered.length} ${STR.of} ${data.total}` : undefined}
        />
        <StatCard
          label={STR.thisWeek}
          value={data ? String(weekCount) : "—"}
          unit="+NEW"
          sub={weekRangeLabel()}
        />
        <SourceChart leads={data?.items ?? []} />
      </div>

      {/* toolbar */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
          <span
            style={{
              position: "absolute",
              insetInlineStart: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-3)",
            }}
          >
            {I.search({ size: 17 })}
          </span>
          <input
            className="inp"
            style={{ paddingInlineStart: 38 }}
            placeholder={STR.search}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        {isAdmin && (
          <select
            className="inp"
            style={{ width: "auto" }}
            value={fEmp}
            onChange={(e) => setFEmp(e.target.value)}
          >
            <option value="all">{STR.allEmployees}</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.full_name || e.email || "— invite pending —"}
              </option>
            ))}
          </select>
        )}
        <select
          className="inp"
          style={{ width: "auto" }}
          value={fSrc}
          onChange={(e) => setFSrc(e.target.value)}
        >
          <option value="all">{STR.allSources}</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          className="inp"
          style={{ width: "auto" }}
          value={fRange}
          onChange={(e) => setFRange(e.target.value as "all" | "week")}
        >
          <option value="all">{STR.allTime}</option>
          <option value="week">{STR.thisWeek}</option>
        </select>
        <button type="button" className="btn btn-ghost" onClick={exportCSV}>
          {I.download({ size: 17 })}
          {STR.exportCsv}
        </button>
      </div>

      {error && (
        <div
          style={{
            color: "#b91c1c",
            fontSize: 13,
            border: "1px solid #fecaca",
            background: "#fef2f2",
            borderRadius: "var(--r-sm)",
            padding: 10,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {/* table / states */}
      {loading && <TableSkeleton />}
      {!loading &&
        (filtered.length === 0 ? (
          <StateBlock
            icon={I.inbox({ size: 24 })}
            title={STR.noLeads}
            sub={STR.noLeadsSub}
            action={
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={clearFilters}
                style={{ marginTop: 4 }}
              >
                {STR.clear}
              </button>
            }
          />
        ) : (
          <>
            <div
              className="mono"
              style={{
                fontSize: 10,
                letterSpacing: ".14em",
                color: "var(--text-3)",
                marginBottom: 8,
              }}
            >
              {STR.drawerHint.toUpperCase()}
            </div>
            <LeadsTable leads={filtered} isAdmin={isAdmin} onRow={setSelected} />
          </>
        ))}

      {data && data.total > PAGE_SIZE && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 13,
            color: "var(--text-2)",
            marginTop: 14,
          }}
        >
          <span>
            {STR.showing} {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} {STR.of}{" "}
            {data.total}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← Prev
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={offset + PAGE_SIZE >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {selected && (
        <LeadDrawer
          lead={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onDeleted={(deletedId) => {
            setSelected(null);
            setData((d) =>
              d
                ? {
                    ...d,
                    items: d.items.filter((l) => l.id !== deletedId),
                    total: Math.max(0, d.total - 1),
                  }
                : d,
            );
          }}
          onUpdated={(updated) => {
            setSelected(updated);
            setData((d) =>
              d ? { ...d, items: d.items.map((l) => (l.id === updated.id ? updated : l)) } : d,
            );
          }}
        />
      )}
    </>
  );
}

/* ============================================================ */

function weekRangeLabel(): string {
  const now = new Date();
  const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const fmt = (d: Date) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  return `${fmt(start)} → ${fmt(now)}`;
}

function StatCard({
  label,
  value,
  unit,
  sub,
}: {
  label: string;
  value: string;
  unit?: string;
  sub?: string;
}) {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: "18px 20px",
        flex: 1,
        minWidth: 150,
      }}
    >
      <div className="tlm">{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 7, marginTop: 10 }}>
        <span
          className="tnum"
          style={{ fontSize: 34, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1 }}
        >
          {value}
        </span>
        {unit && (
          <span
            className="mono"
            style={{ fontSize: 11, color: "var(--text-3)", letterSpacing: ".1em" }}
          >
            {unit}
          </span>
        )}
      </div>
      {sub && (
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            color: "var(--text-3)",
            marginTop: 8,
            letterSpacing: ".06em",
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function SourceChart({ leads }: { leads: LeadAdmin[] }) {
  const counts = useMemo<Array<[string, number]>>(() => {
    const m = new Map<string, number>();
    leads.forEach((l) => {
      const k = l.source_tag ?? NULL_SOURCE;
      m.set(k, (m.get(k) ?? 0) + 1);
    });
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [leads]);
  const max = Math.max(1, ...counts.map((c) => c[1]));
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: "18px 20px",
        flex: 2,
        minWidth: 280,
      }}
    >
      <div className="tlm">{STR.bySource}</div>
      {counts.length === 0 ? (
        <div
          style={{
            fontSize: 13,
            color: "var(--text-3)",
            marginTop: 18,
            height: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          No data yet
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: 14,
            height: 120,
            marginTop: 18,
          }}
        >
          {counts.map(([s, n]) => (
            <div
              key={s}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 8,
                height: "100%",
                justifyContent: "flex-end",
                minWidth: 0,
              }}
            >
              <span
                className="mono tnum"
                style={{ fontSize: 11, color: "var(--text-2)", fontWeight: 600 }}
              >
                {n}
              </span>
              <div
                title={`${s}: ${n}`}
                style={{
                  width: "100%",
                  maxWidth: 40,
                  height: `${(n / max) * 78}px`,
                  minHeight: 3,
                  background: "var(--signal)",
                  borderRadius: "4px 4px 0 0",
                  transition: "height .5s var(--ease)",
                }}
              />
              <span
                className="mono"
                style={{
                  fontSize: 8.5,
                  letterSpacing: ".06em",
                  color: "var(--text-3)",
                  textAlign: "center",
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  whiteSpace: "nowrap",
                }}
              >
                {s}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { color: string; dot: string; bd: string }> = {
    captured: { color: "var(--signal-ink)", dot: "var(--signal)", bd: "var(--line)" },
    partial: { color: "var(--text-2)", dot: "var(--text-3)", bd: "var(--line)" },
    "opted-out": { color: "var(--text-3)", dot: "var(--text-3)", bd: "var(--line-2)" },
  };
  const m = map[status] ?? map.captured!;
  return (
    <span className="pill" style={{ color: m.color, borderColor: m.bd }}>
      <span className="dot" style={{ background: m.dot }} />
      {status}
    </span>
  );
}

function LeadsTable({
  leads,
  isAdmin,
  onRow,
}: {
  leads: LeadAdmin[];
  isAdmin: boolean;
  onRow: (l: LeadAdmin) => void;
}) {
  const cols: Array<{ k: string; label: string }> = [
    { k: "name", label: STR.name },
    { k: "company", label: STR.company },
    { k: "wa", label: STR.wa },
    { k: "city", label: STR.city },
    { k: "landline", label: STR.landline },
    { k: "website", label: STR.website },
    ...(isAdmin ? [{ k: "emp", label: STR.emp }] : []),
    { k: "source", label: STR.source },
    { k: "captured", label: STR.captured },
    { k: "status", label: STR.status },
  ];
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        overflow: "hidden",
      }}
    >
      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            /* Bumped to accommodate the three new card-detail columns;
               wrapper above stays scrollable on narrow viewports. */
            minWidth: isAdmin ? 1180 : 1060,
          }}
        >
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line)" }}>
              {cols.map((c) => (
                <th
                  key={c.k}
                  className="mono"
                  style={{
                    textAlign: "left",
                    padding: "13px 16px",
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: ".16em",
                    textTransform: "uppercase",
                    color: "var(--text-3)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr
                key={l.id}
                className="lead-row"
                onClick={() => onRow(l)}
                style={{
                  borderBottom: "1px solid var(--line-2)",
                  cursor: "pointer",
                  transition: "background .15s",
                }}
              >
                <td style={{ padding: "13px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {l.has_card_image && (
                      <img
                        src={adminCardImageUrl(l.id)}
                        alt=""
                        loading="lazy"
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          objectFit: "cover",
                          border: "1px solid var(--line)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {l.name || <span style={{ color: "var(--text-3)" }}>—</span>}
                      </div>
                      <div
                        className="mono"
                        style={{ fontSize: 10.5, color: "var(--text-3)", marginTop: 2 }}
                      >
                        {l.id.slice(0, 8)}
                      </div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: "13px 16px", fontSize: 13.5, color: "var(--text-2)" }}>
                  {l.company || <span style={{ color: "var(--text-3)" }}>—</span>}
                </td>
                <td
                  className="mono"
                  style={{
                    padding: "13px 16px",
                    fontSize: 12,
                    color: "var(--text-2)",
                    direction: "ltr",
                  }}
                >
                  +{l.wa_id}
                </td>
                <td
                  style={{
                    padding: "13px 16px",
                    fontSize: 13,
                    color: "var(--text-2)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {l.city || <span style={{ color: "var(--text-3)" }}>—</span>}
                </td>
                <td
                  className="mono"
                  style={{
                    padding: "13px 16px",
                    fontSize: 12,
                    color: "var(--text-2)",
                    direction: "ltr",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmtSaPhone(l.landline) || <span style={{ color: "var(--text-3)" }}>—</span>}
                </td>
                <td
                  className="mono"
                  style={{
                    padding: "13px 16px",
                    fontSize: 12,
                    color: "var(--text-2)",
                    direction: "ltr",
                    maxWidth: 220,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {l.website_url ? (
                    <a
                      href={l.website_url}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: "var(--text-2)", textDecoration: "none" }}
                      title={l.website_url}
                    >
                      {l.website_url.replace(/^https?:\/\//, "").replace(/^www\./, "")}
                    </a>
                  ) : (
                    <span style={{ color: "var(--text-3)" }}>—</span>
                  )}
                </td>
                {isAdmin && (
                  <td
                    style={{
                      padding: "13px 16px",
                      fontSize: 13,
                      color: "var(--text-2)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {l.employee_name || <span style={{ color: "var(--text-3)" }}>—</span>}
                  </td>
                )}
                <td style={{ padding: "13px 16px" }}>
                  <span
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      letterSpacing: ".08em",
                      color: "var(--text-2)",
                      border: "1px solid var(--line)",
                      borderRadius: 6,
                      padding: "3px 7px",
                    }}
                  >
                    {l.source_tag ?? NULL_SOURCE}
                  </span>
                </td>
                <td
                  className="mono tnum"
                  style={{
                    padding: "13px 16px",
                    fontSize: 12,
                    color: "var(--text-2)",
                    whiteSpace: "nowrap",
                    direction: "ltr",
                  }}
                >
                  {new Date(l.created_at).toISOString().slice(0, 10)}
                </td>
                <td style={{ padding: "13px 16px" }}>
                  <StatusPill status={l.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TableSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: "8px 16px",
      }}
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            padding: "14px 0",
            borderBottom: i < rows - 1 ? "1px solid var(--line-2)" : "none",
          }}
        >
          <div className="skel" style={{ width: "22%", height: 13 }} />
          <div className="skel" style={{ width: "18%", height: 13 }} />
          <div className="skel" style={{ width: "20%", height: 13 }} />
          <div className="skel" style={{ flex: 1, height: 13 }} />
          <div className="skel" style={{ width: 72, height: 22, borderRadius: 999 }} />
        </div>
      ))}
    </div>
  );
}

function StateBlock({
  icon,
  title,
  sub,
  action,
}: {
  icon: ReactNode;
  title: string;
  sub: string;
  action?: ReactNode;
}) {
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: "56px 28px",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          border: "1px solid var(--line)",
          color: "var(--text-3)",
          background: "var(--field)",
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, marginTop: 4 }}>{title}</div>
      <div style={{ fontSize: 13.5, color: "var(--text-2)", maxWidth: 320, lineHeight: 1.5 }}>
        {sub}
      </div>
      {action}
    </div>
  );
}

/* `wa_id` on the server is NOT NULL; in the form we allow transient null
   (e.g. while admin is retyping it) and reject empty-string on submit. */
type LeadEditable = {
  wa_id: string | null;
  name: string | null;
  title: string | null;
  company: string | null;
  email: string | null;
  mobile: string | null;
  landline: string | null;
  address: string | null;
  city: string | null;
  linkedin_url: string | null;
  website_url: string | null;
  notes: string | null;
};

const EDIT_KEYS: ReadonlyArray<keyof LeadEditable> = [
  "wa_id",
  "name",
  "title",
  "company",
  "email",
  "mobile",
  "landline",
  "address",
  "city",
  "linkedin_url",
  "website_url",
  "notes",
];

function leadToForm(lead: LeadAdmin): LeadEditable {
  return {
    wa_id: lead.wa_id,
    name: lead.name,
    title: lead.title,
    company: lead.company,
    email: lead.email,
    mobile: lead.mobile,
    landline: lead.landline,
    address: lead.address,
    city: lead.city,
    linkedin_url: lead.linkedin_url,
    website_url: lead.website_url,
    notes: lead.notes,
  };
}

function LeadDrawer({
  lead,
  isAdmin,
  onClose,
  onDeleted,
  onUpdated,
}: {
  lead: LeadAdmin;
  isAdmin: boolean;
  onClose: () => void;
  onDeleted: (id: string) => void;
  onUpdated: (lead: LeadAdmin) => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<LeadEditable>(() => leadToForm(lead));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  /* When the drawer is fed a different lead (e.g. user clicks another row
     without closing), reset the editor. */
  useEffect(() => {
    setForm(leadToForm(lead));
    setEditing(false);
    setSaveError(null);
  }, [lead]);

  function setField<K extends keyof LeadEditable>(key: K, value: LeadEditable[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  /* Send only the fields the admin actually changed. Empty-string input
     becomes null so clearing a field works. wa_id is NOT NULL on the
     server — refuse to even send a cleared wa_id. */
  function dirtyPayload(): { payload: Partial<LeadEditable>; error: string | null } {
    const out: Partial<LeadEditable> = {};
    for (const k of EDIT_KEYS) {
      const original = (lead[k] ?? null) as string | null;
      const next = ((form[k] ?? "") as string).trim();
      const nextOrNull = next === "" ? null : next;
      if (nextOrNull !== original) (out as Record<string, unknown>)[k] = nextOrNull;
    }
    if ("wa_id" in out && out.wa_id == null) {
      return { payload: {}, error: "WhatsApp number cannot be empty." };
    }
    return { payload: out, error: null };
  }

  async function handleSave() {
    const { payload, error: dirtyErr } = dirtyPayload();
    if (dirtyErr) {
      setSaveError(dirtyErr);
      return;
    }
    if (Object.keys(payload).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await adminApi.updateLead(
        lead.id,
        payload as Partial<LeadAdmin>,
      );
      onUpdated(updated);
      setEditing(false);
    } catch (e: unknown) {
      setSaveError(
        e instanceof HttpError
          ? `HTTP ${e.status}: ${e.message}`
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setForm(leadToForm(lead));
    setEditing(false);
    setSaveError(null);
  }

  async function handleDelete() {
    const confirmed = window.confirm(
      `Delete this lead?\n\n${lead.name || "—"} · ${lead.company || "—"}\n\nThis cannot be undone.`,
    );
    if (!confirmed) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await adminApi.deleteLead(lead.id);
      onDeleted(lead.id);
    } catch (e: unknown) {
      setDeleteError(
        e instanceof HttpError
          ? `HTTP ${e.status}: ${e.message}`
          : e instanceof Error
            ? e.message
            : String(e),
      );
      setDeleting(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60 }}>
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(6,9,15,.5)",
          backdropFilter: "blur(2px)",
          animation: "fadein .2s",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "min(440px, 92vw)",
          background: "var(--field)",
          borderInlineStart: "1px solid var(--line)",
          boxShadow: "var(--elev)",
          display: "flex",
          flexDirection: "column",
          animation: "drawerin .28s var(--ease)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 22px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div>
            <div
              className="mono"
              style={{ fontSize: 10.5, letterSpacing: ".18em", color: "var(--text-3)" }}
            >
              {lead.id.slice(0, 8)}
            </div>
            <h2 style={{ margin: "5px 0 0", fontSize: 20, fontWeight: 700, letterSpacing: "-.02em" }}>
              {lead.name || "—"}
            </h2>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {isAdmin && !editing && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ height: 38, padding: "0 12px", fontSize: 13 }}
                onClick={() => setEditing(true)}
                aria-label="Edit lead"
              >
                Edit
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ width: 38, padding: 0, height: 38 }}
              onClick={onClose}
              aria-label="Close"
            >
              {I.x({ size: 18 })}
            </button>
          </div>
        </div>
        <div style={{ padding: "8px 22px 22px", overflowY: "auto", flex: 1 }}>
          {!editing && (
            <>
              <DrawerField label={STR.company} value={lead.company} />
              <DrawerField label={STR.email} value={lead.email} mono ltr />
              <DrawerField label={STR.wa} value={`+${lead.wa_id}`} mono ltr />
              {isAdmin && <DrawerField label={STR.empCol} value={lead.employee_name} />}
              <DrawerField label={STR.sourceCol} value={lead.source_tag ?? NULL_SOURCE} mono />
              <DrawerField
                label={STR.capturedCol}
                value={new Date(lead.created_at).toLocaleString()}
                mono
                ltr
              />
              <DrawerField label={STR.status} value={lead.status} mono />

              {hasCardDetails(lead) && (
                <div style={{ marginTop: 24, paddingTop: 14, borderTop: "1px dashed var(--line)" }}>
                  <div className="tlm" style={{ marginBottom: 4 }}>
                    {STR.cardDetails}
                  </div>
                  {lead.has_card_image && (
                    <img
                      src={adminCardImageUrl(lead.id)}
                      alt="Scanned business card"
                      style={{
                        width: "100%",
                        maxWidth: 340,
                        borderRadius: "var(--r-md)",
                        border: "1px solid var(--line)",
                        marginBottom: 14,
                        display: "block",
                      }}
                    />
                  )}
                  {lead.title && <DrawerField label={STR.title} value={lead.title} />}
                  {lead.mobile && (
                    <DrawerField label={STR.mobile} value={fmtSaPhone(lead.mobile)} mono ltr />
                  )}
                  {lead.landline && (
                    <DrawerField label={STR.landline} value={fmtSaPhone(lead.landline)} mono ltr />
                  )}
                  {lead.city && <DrawerField label={STR.city} value={lead.city} />}
                  {lead.address && <DrawerField label={STR.address} value={lead.address} />}
                  {lead.linkedin_url && (
                    <DrawerField label={STR.linkedin} value={lead.linkedin_url} mono ltr />
                  )}
                  {lead.website_url && (
                    <DrawerField label={STR.website} value={lead.website_url} mono ltr />
                  )}
                </div>
              )}

              <div style={{ marginTop: 20 }}>
                <div className="tlm" style={{ marginBottom: 9 }}>
                  {STR.notes}
                </div>
                <div
                  style={{
                    border: "1px dashed var(--line)",
                    borderRadius: "var(--r-md)",
                    padding: 16,
                    background: "var(--surface)",
                    color: "var(--text-2)",
                    fontSize: 13,
                    lineHeight: 1.4,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {lead.notes || (
                    <span className="mono" style={{ color: "var(--text-3)" }}>
                      {STR.noNotes}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}

          {editing && (
            <>
              <EditField label={STR.name} value={form.name} onChange={(v) => setField("name", v)} />
              <EditField label={STR.title} value={form.title} onChange={(v) => setField("title", v)} />
              <EditField label={STR.company} value={form.company} onChange={(v) => setField("company", v)} />
              <EditField label={STR.email} value={form.email} onChange={(v) => setField("email", v)} type="email" mono />
              <EditField
                label={STR.wa}
                value={form.wa_id}
                onChange={(v) => setField("wa_id", v)}
                mono
                placeholder="+966 50 685 3876"
                hint="Identity key for WhatsApp routing. Required."
              />
              <EditField label={STR.mobile} value={form.mobile} onChange={(v) => setField("mobile", v)} mono placeholder="+966 50 685 3876" />
              <EditField label={STR.landline} value={form.landline} onChange={(v) => setField("landline", v)} mono placeholder="+966 13 882 2666" />
              <EditField label={STR.city} value={form.city} onChange={(v) => setField("city", v)} />
              <EditField label={STR.address} value={form.address} onChange={(v) => setField("address", v)} />
              <EditField label={STR.linkedin} value={form.linkedin_url} onChange={(v) => setField("linkedin_url", v)} mono placeholder="https://linkedin.com/in/…" />
              <EditField label={STR.website} value={form.website_url} onChange={(v) => setField("website_url", v)} mono placeholder="https://…" />

              <div style={{ marginTop: 18 }}>
                <div className="tlm" style={{ marginBottom: 8 }}>
                  {STR.notes}
                </div>
                <textarea
                  value={form.notes ?? ""}
                  onChange={(e) => setField("notes", e.target.value)}
                  rows={4}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid var(--line)",
                    borderRadius: "var(--r-md)",
                    padding: 12,
                    background: "var(--surface)",
                    color: "var(--text)",
                    fontSize: 13,
                    fontFamily: "inherit",
                    lineHeight: 1.45,
                    resize: "vertical",
                  }}
                />
              </div>

              {saveError && (
                <div
                  style={{
                    marginTop: 12,
                    fontSize: 12,
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    borderRadius: "var(--r-sm)",
                    padding: 8,
                  }}
                >
                  {saveError}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  style={{ minWidth: 120 }}
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </>
          )}

          {/* Danger zone — admin-only hard delete for removing test rows */}
          {isAdmin && !editing && (
            <div style={{ marginTop: 28, paddingTop: 18, borderTop: "1px dashed var(--line)" }}>
              <div
                className="tlm"
                style={{ color: "#b91c1c", marginBottom: 8 }}
              >
                Danger zone
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-2)", marginBottom: 10 }}>
                Permanently delete this lead. Use this for test rows or PDPL
                erasure requests. Cannot be undone.
              </div>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={deleting}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 13px",
                  fontFamily: "var(--font-sans)",
                  fontSize: 13,
                  fontWeight: 600,
                  borderRadius: "var(--r-sm)",
                  border: "1px solid #fecaca",
                  background: "#fef2f2",
                  color: "#b91c1c",
                  cursor: deleting ? "wait" : "pointer",
                  opacity: deleting ? 0.6 : 1,
                }}
              >
                {I.x({ size: 15 })}
                {deleting ? "Deleting…" : "Delete lead"}
              </button>
              {deleteError && (
                <div
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    color: "#b91c1c",
                    border: "1px solid #fecaca",
                    background: "#fef2f2",
                    borderRadius: "var(--r-sm)",
                    padding: 8,
                  }}
                >
                  {deleteError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function hasCardDetails(lead: LeadAdmin): boolean {
  return Boolean(
    lead.title ||
      lead.mobile ||
      lead.landline ||
      lead.address ||
      lead.city ||
      lead.linkedin_url ||
      lead.website_url ||
      lead.has_card_image,
  );
}

/* Format canonical Saudi digits (e.g. "966506853876") for display. Anything
   that doesn't look like our 966 + 8-9 trailing digits canonical form is
   passed through unchanged — defensive against pre-migration rows. */
function fmtSaPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!/^966\d{8,9}$/.test(raw)) return raw;
  const tail = raw.slice(3);
  // Mobile (5XXXXXXXX, 9 digits) → +966 5X XXX XXXX
  if (tail.length === 9 && tail.startsWith("5")) {
    return `+966 ${tail.slice(0, 2)} ${tail.slice(2, 5)} ${tail.slice(5)}`;
  }
  // Landline (1-2 digit area + 7 digit subscriber, total 8-9)
  if (tail.length === 9) {
    return `+966 ${tail.slice(0, 2)} ${tail.slice(2, 5)} ${tail.slice(5)}`;
  }
  return `+966 ${tail.slice(0, 1)} ${tail.slice(1, 4)} ${tail.slice(4)}`;
}

function EditField({
  label,
  value,
  onChange,
  mono,
  type,
  placeholder,
  hint,
}: {
  label: string;
  value: string | null | undefined;
  onChange: (v: string | null) => void;
  mono?: boolean;
  type?: string;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div style={{ padding: "10px 0", borderBottom: "1px solid var(--line-2)" }}>
      <label
        className="mono"
        style={{
          display: "block",
          fontSize: 10,
          letterSpacing: ".16em",
          textTransform: "uppercase",
          color: "var(--text-3)",
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      <input
        type={type ?? "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={mono ? "mono" : ""}
        style={{
          width: "100%",
          boxSizing: "border-box",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-sm)",
          padding: "8px 10px",
          background: "var(--surface)",
          color: "var(--text)",
          fontSize: 13.5,
          fontFamily: "inherit",
          direction: mono ? "ltr" : "inherit",
        }}
      />
      {hint && (
        <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

function DrawerField({
  label,
  value,
  mono,
  ltr,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  ltr?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 16,
        padding: "12px 0",
        borderBottom: "1px solid var(--line-2)",
      }}
    >
      <span
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: ".16em",
          textTransform: "uppercase",
          color: "var(--text-3)",
        }}
      >
        {label}
      </span>
      <span
        className={mono ? "mono" : ""}
        style={{
          fontSize: 13.5,
          color: "var(--text)",
          textAlign: "right",
          direction: ltr ? "ltr" : "inherit",
          maxWidth: "60%",
          wordBreak: "break-word",
        }}
      >
        {value || <span style={{ color: "var(--text-3)" }}>—</span>}
      </span>
    </div>
  );
}
